// The assistant's SSE wire, read and decoded in one place.
//
// EXTRACTED FROM `aiAgent.ts`, which was over the file-size limit and, more to the
// point, had grown a SECOND copy of the read loop when the brief-tuning stream was
// added. Two loops reading the same framing is two places to get the reader-cancel
// wrong — and that one matters: an un-cancelled reader holds its connection until
// GC runs, so abort/retry cycles exhaust the per-origin pool.
//
// What lives here is everything that knows the wire: the event union, the frame
// decoder, and the loop. What stays in `aiAgent.ts` is the endpoints.
//
// EVERY FRAME IS READ DEFENSIVELY. A field this build does not understand is
// dropped rather than passed on half-typed — and a `briefProposal`'s section keys
// are checked against this build's own list, because the card drawn from them is
// what the reader presses Save on.

// Type-only, so the cycle with `aiAgent.ts` is erased at compile time — the
// runtime dependency runs one way, endpoints → wire.
import type { AiChart, AiGuideImage, AiTable } from './aiAgent';
import { BRIEF_SECTION_KEYS, type BriefSectionKey } from '@constants/briefSections';
import { readChecks, readFollowUps, type AiCheck } from '@d-orca/shared/ai-answer';
import { readLink, type AiLink } from '@d-orca/shared/navigation';
import { sseFrames } from './sseFrames';

/**
 * Streaming chat event surface — discriminated on `type` so the page can
 * switch and update local state accordingly. Mirrors exactly what
 * `backend/src/api/routes/aiAgent.sse.ts` emits:
 * `metadata | status | text | table | chart | link | checks | followUps | error | done`.
 */
export type AiChatStreamEvent =
  /**
   * First frame of every turn. Carries the thread id plus the ids of the two
   * messages this turn creates — both minted before the model is called, so
   * the page can label its optimistic bubbles without a refetch. The two
   * message ids are typed optional only so a frame missing them still
   * delivers the conversation id (which drives the sidebar) instead of being
   * dropped whole.
   */
  | {
      type: 'metadata';
      conversationId: string;
      questionId?: string;
      answerId?: string;
    }
  /**
   * A tool, by its raw id (`query_issues`, `render_table`, …). Never
   * rendered as-is — `aiStatusLabelKey` maps it onto a localised sentence.
   *
   * `failed` distinguishes the two things the backend sends on this frame:
   * false means the tool just STARTED (this is what carries the wait), true
   * means it finished with an error. A failed tool is swallowed into an
   * envelope handed back to the model, so the turn still ends with a
   * plausible answer written around the gap — the flag is the only way the
   * client learns the answer is missing data.
   */
  /**
   * `label` is a tool id, or — for a composite — one of its SECTIONS
   * (`brief.safety`). `degraded` marks a section whose source could not be
   * read; it is absent rather than false on the common frame.
   */
  | { type: 'status'; label: string; failed: boolean; degraded?: boolean }
  /**
   * A piece of the answer, to APPEND. Paragraph breaks between the model's
   * rounds are already in it.
   */
  | { type: 'text'; content: string }
  /** A drawing, with the id its marker in the answer names. */
  | { type: 'table'; table: AiTable; id?: string }
  | { type: 'chart'; chart: AiChart; id?: string }
  | { type: 'image'; image: AiGuideImage; id?: string }
  /** A link the answer offers, already read against the catalog. */
  | { type: 'link'; link: AiLink }
  /**
   * What the answer checked — its receipt. At most once a turn, after the last
   * of the answer and before `done`; never sent empty.
   */
  | { type: 'checks'; checks: AiCheck[] }
  /**
   * Next questions the model suggests. A later frame REPLACES an earlier one in
   * the same turn: the model may call the tool twice, and the second call is
   * what it meant. Untrusted text, sent on a tap — see `AiFollowUps` for why
   * that is safe only while every tool reads.
   */
  | { type: 'followUps'; questions: string[] }
  /**
   * The tuning agent's PROPOSED brief preference, for the reader to accept, edit
   * or decline. It is not a save — see `AiBriefProposal`.
   */
  | { type: 'briefProposal'; proposal: AiBriefProposal }
  /**
   * The wire frame also carries a `message`, and it is deliberately
   * dropped here: it is internal English prose from inside the service and
   * must never reach a Japanese user. `code` is the whole point of the
   * shape — `aiErrorMessageKey` maps it to a translated string.
   */
  | { type: 'error'; code: string }
  /**
   * `truncated` means the model's output limit cut the answer off. `aborted`
   * means the turn's deadline ended it: the server stored NO answer, so what
   * is on screen cannot be rated or reopened. Absent rather than false.
   */
  | { type: 'done'; truncated: boolean; aborted?: boolean };


/** Fallback code for an `error` frame that arrives without one. */
const UNKNOWN_ERROR_CODE = 'UNKNOWN';

/**
 * A morning-brief preference the tuning assistant SUGGESTS.
 *
 * IT IS NOT A SAVE, and the distinction is a security boundary rather than a
 * nicety. The assistant that drafts this has no write tool at all; the card the
 * reader sees is drawn from this, and the save is the `updateBriefPreferences` PUT
 * their own click issues, carrying whatever they left in the field. So a proposal
 * steered by text nobody vouched for is a card somebody declines, never a stored
 * instruction (Part D of
 * `docs/specs/2026-09-17-customizable-morning-brief-design.md`).
 */
export interface AiBriefProposal {
  interest: string;
  mutedSections: BriefSectionKey[];
  /** One line for the reader on what was taken from what they said. */
  rationale: string;
}

/**
 * A proposed brief preference, narrowed at the boundary.
 *
 * The section keys are checked against this build's own list: a key it does not
 * know would render as a switch for a section that is not there, and would then be
 * PUT back to the server as part of the reader's save.
 */
function readProposal(value: unknown): AiBriefProposal | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as { interest?: unknown; mutedSections?: unknown; rationale?: unknown };
  if (typeof raw.interest !== 'string' || raw.interest === '') return null;
  const muted = Array.isArray(raw.mutedSections) ? raw.mutedSections : [];
  return {
    interest: raw.interest,
    mutedSections: muted.filter((key): key is BriefSectionKey =>
      (BRIEF_SECTION_KEYS as readonly unknown[]).includes(key),
    ),
    rationale: typeof raw.rationale === 'string' ? raw.rationale : '',
  };
}

/** A drawing frame's id, when it carries one. Carried, not dropped: without it the drawing cannot be placed. */
function idOf(parsed: Record<string, unknown>): { id?: string } {
  return typeof parsed.id === 'string' ? { id: parsed.id } : {};
}

/** A Help Center asset address, as the backend writes it (`help.service.ts` `assetIdFor`). */
const HELP_ASSET_SRC = /^\/api\/help\/assets\/[0-9a-f]+$/;

function mapSseEvent(event: string, data: string): AiChatStreamEvent | null {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data) as Record<string, unknown>;
  } catch {
    return null;
  }
  switch (event) {
    case 'metadata': {
      const cid = parsed.conversationId;
      if (typeof cid !== 'string') return null;
      return {
        type: 'metadata',
        conversationId: cid,
        questionId: typeof parsed.questionId === 'string' ? parsed.questionId : undefined,
        answerId: typeof parsed.answerId === 'string' ? parsed.answerId : undefined,
      };
    }
    case 'text': {
      const content = parsed.content;
      return typeof content === 'string' && content.length > 0 ? { type: 'text', content } : null;
    }
    case 'status': {
      const label = parsed.label;
      // `degraded` is CARRIED, not dropped: a flag the
      // decoder drops is a flag nobody can see is missing — the status line
      // still reads correctly, and the bug hides behind it. It marks a SECTION
      // of a composite that could not be read (`query_site_brief`), which is
      // not the same as `failed`: the tool worked, one of its sources did not.
      return typeof label === 'string' && label.length > 0
        ? {
            type: 'status',
            label,
            failed: parsed.error === true,
            ...(parsed.degraded === true ? { degraded: true } : {}),
          }
        : null;
    }
    case 'table': {
      const table = parsed.table as AiTable | undefined;
      return table ? { type: 'table', table, ...idOf(parsed) } : null;
    }
    // Once missing, which left a chart off the page until the thread was
    // reopened from the store.
    case 'chart': {
      const chart = parsed.chart as AiChart | undefined;
      return chart ? { type: 'chart', chart, ...idOf(parsed) } : null;
    }
    // A guide screenshot. Its src must be exactly a Help Center asset — the
    // route and a hex id, nothing after it: a frame naming any other address
    // (a `..`, a query, another route) is no image, since the page fetches it
    // with the session cookie.
    case 'image': {
      const image = parsed.image as AiGuideImage | undefined;
      return image && typeof image.src === 'string' && HELP_ASSET_SRC.test(image.src)
        ? { type: 'image', image, ...idOf(parsed) }
        : null;
    }
    // Read against the catalog at the boundary, like everything else a frame
    // carries: a destination this build does not know is no link at all.
    case 'link': {
      const link = readLink(parsed.link);
      return link ? { type: 'link', link } : null;
    }
    // Both read leniently, as a stored thread is: a source this build does
    // not know is dropped, not the receipt; an over-long question is no chip.
    // A frame with nothing left in it is no frame.
    case 'checks': {
      const checks = readChecks(parsed.checks);
      return checks.length > 0 ? { type: 'checks', checks } : null;
    }
    case 'followUps': {
      const questions = readFollowUps(parsed.questions);
      return questions.length > 0 ? { type: 'followUps', questions } : null;
    }
    // The tuning agent's proposed preference. Read defensively at the boundary
    // like every other frame: a proposal missing a field is no proposal, because
    // the card it would draw is what the reader presses Save on.
    case 'briefProposal': {
      const proposal = readProposal(parsed.proposal);
      return proposal ? { type: 'briefProposal', proposal } : null;
    }
    case 'error': {
      // `parsed.message` is read by nobody on purpose — see the union.
      const code = parsed.code;
      return {
        type: 'error',
        code: typeof code === 'string' && code ? code : UNKNOWN_ERROR_CODE,
      };
    }
    case 'done':
      return {
        type: 'done',
        truncated: parsed.truncated === true,
        ...(parsed.aborted === true ? { aborted: true } : {}),
      };
    default:
      return null;
  }
}

/** Drain an SSE response onto `onEvent`, decoding each frame. */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: AiChatStreamEvent) => void,
): Promise<void> {
  for await (const frame of sseFrames(body)) {
    const event = mapSseEvent(frame.event, frame.data);
    if (event) onEvent(event);
  }
}
